import { zodResolver } from "@hookform/resolvers/zod";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { ApiError } from "@/api/error";
import {
  useConfirmReset,
  useRequestResetCode,
  useVerifyResetCode,
} from "@/shared/hooks/use-reset-password";
import { toast } from "@/app/store/toast.store";
import { StepIndicator } from "@/features/auth/components/StepIndicator";
import {
  resetPasswordStep1Schema,
  resetPasswordStep2Schema,
  resetPasswordStep3Schema,
  type ResetPasswordStep1Values,
  type ResetPasswordStep2Values,
  type ResetPasswordStep3Values,
} from "@/features/auth/schemas/reset-password.schema";
import { useTimer } from "@/shared/hooks/use-timer";
import { ShcLogo } from "@/components/brand/ShcLogo";

// Map a thrown error → the i18n KEY the form renders (the JSX wraps the message in t()). The BE also returns
// the Korean message, but the FE owns the displayed label (wire code → key) per the Korean-only i18n policy.
//
// SHCT-322: this table is also the inline/toast switch. A code listed here is a Validation or Business error
// the user can act on → INLINE under its field (PRD 00 §3.3 조건분기). Everything else — network, the BE's
// EMAIL_SEND_FAILED, 5xx, unknown — is a System Error → **red toast, never inline** (PRD 00 §8.2, PRD 11 §4.2,
// CLAUDE.md §7.1). Mirrors MedicalLoginPage and admin-web's AdminResetPasswordPage.
const CODE_TO_MESSAGE_KEY: Record<string, string> = {
  AUTH_RESET_NO_ACCOUNT: "validation:auth.emailNotFound",
  RATE_LIMITED: "validation:auth.rateLimited",
  OTP_INVALID: "validation:auth.codeMismatch",
  OTP_EXPIRED: "validation:auth.codeExpired",
  AUTH_PASSWORD_POLICY: "validation:auth.passwordRule",
  AUTH_PASSWORD_REUSE: "validation:auth.passwordReuse",
  AUTH_PASSWORD_CONFIRM_MISMATCH: "validation:auth.passwordMismatch",
  AUTH_RESET_TOKEN_INVALID: "validation:auth.resetTokenInvalid",
};

/** The inline i18n key for a field-level error, or `null` when the error is a System Error (→ toast). */
export const inlineMessageKey = (error: unknown): string | null => {
  const code = error instanceof ApiError ? error.code : "";
  return CODE_TO_MESSAGE_KEY[code] ?? null;
};

/**
 * i18n key for the System-Error toast copy, per PRD 00 §8.2:
 * no server response (and the BE's own "OTP mail could not be sent") → "서버와의 연결이 원활하지 않습니다…",
 * anything else (5xx / unknown) → "시스템 오류가 발생했습니다. 관리자에게 문의하세요."
 */
export const systemErrorKey = (error: unknown): string => {
  const code = error instanceof ApiError ? error.code : "NETWORK";
  return code === "NETWORK" || code === "EMAIL_SEND_FAILED"
    ? "common:error.serverUnreachable"
    : "auth:resetPassword.errors.systemError";
};

type Step = 1 | 2 | 3 | "done";

export const ResetPasswordPage = () => {
  // "common" is listed explicitly because the System-Error toast reads `common:error.serverUnreachable`
  // (same set as MedicalLoginPage).
  const { t } = useTranslation(["auth", "common", "validation"]);
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(1);
  const [email, setEmail] = useState("");
  const [resetToken, setResetToken] = useState("");
  const {
    timerLeft,
    isExpired: isTimerExpired,
    startTimer,
    formatTimer,
  } = useTimer();

  // React Query mutations
  const requestResetCodeMutation = useRequestResetCode();
  const verifyResetCodeMutation = useVerifyResetCode();
  const confirmResetMutation = useConfirmReset();

  // System Error → red toast (SHCT-322). Never also inline: QC's expected behavior is toast only.
  const showSystemError = (error: unknown) => toast.error(t(systemErrorKey(error)));

  // ── Step 1: Email ──
  const step1Form = useForm<ResetPasswordStep1Values>({
    resolver: zodResolver(resetPasswordStep1Schema),
    mode: "onTouched",
    defaultValues: { email: "" },
  });

  const onStep1 = (data: ResetPasswordStep1Values) => {
    requestResetCodeMutation.mutate(data.email, {
      onSuccess: () => {
        setEmail(data.email);
        setStep(2);
        startTimer();
      },
      onError: (error) => {
        const key = inlineMessageKey(error);
        if (key) toast.error(t(key));
        else showSystemError(error);
      },
    });
  };

  // ── Step 2: Verify Code ──
  const step2Form = useForm<ResetPasswordStep2Values>({
    resolver: zodResolver(resetPasswordStep2Schema),
    mode: "onTouched",
    defaultValues: { code: "" },
  });
  // OTP is numeric-only (SHCT-336) — strip every non-digit on type/paste before
  // RHF reads the value, matching PhoneVerificationCodeSection's behavior.
  const codeReg = step2Form.register("code");

  const onStep2 = (data: ResetPasswordStep2Values) => {
    verifyResetCodeMutation.mutate({ email, code: data.code }, {
      onSuccess: ({ resetToken: token }) => {
        setResetToken(token);
        setStep(3);
      },
      onError: (error) => {
        const key = inlineMessageKey(error);
        if (key) toast.error(t(key));
        else showSystemError(error);
      },
    });
  };

  const handleResend = () => {
    requestResetCodeMutation.mutate(email, {
      onSuccess: () => {
        startTimer();
        step2Form.reset();
      },
      onError: (error) => {
        const key = inlineMessageKey(error);
        if (key) toast.error(t(key));
        else showSystemError(error);
      },
    });
  };

  // ── Step 3: New Password ──
  const step3Form = useForm<ResetPasswordStep3Values>({
    resolver: zodResolver(resetPasswordStep3Schema),
    mode: "onTouched",
    defaultValues: { password: "", confirmPassword: "" },
  });

  const onStep3 = (data: ResetPasswordStep3Values) => {
    confirmResetMutation.mutate(
      { resetToken, password: data.password, confirmPassword: data.confirmPassword },
      {
        onSuccess: () => setStep("done"),
        onError: (error) => {
          const key = inlineMessageKey(error);
          if (key) toast.error(t(key));
          else showSystemError(error);
        },
      },
    );
  };

  // ── Render ──
  const steps = [1, 2, 3];

  return (
    <main className="auth-page">
      <div className="auth-card">
        {/* Logo */}
        <div className="auth-card__logo">
          <div className="auth-card__logo-icon">
            <ShcLogo />
          </div>
          <h1 className="auth-card__title">
            {t("resetPassword.title", { ns: "auth" })}
          </h1>
        </div>

        {/* Step Indicator — stays visible through the done screen (SHCT-311):
            on "done" it holds at step 3 (①② done, ③ active), matching the prototype. */}
        <StepIndicator
          steps={steps}
          currentStep={step === "done" ? 3 : step}
          t={t}
        />

        {step !== "done" && (
          <>
            {/* Step 1 */}
            {step === 1 && (
              <form onSubmit={step1Form.handleSubmit(onStep1)} noValidate>
                <div className="form-field">
                  <label className="form-field__label">
                    {t("resetPassword.emailLabel", { ns: "auth" })}
                  </label>
                  <input
                    type="email"
                    autoComplete="email"
                    placeholder="example@uuh.ulsan.kr"
                    {...step1Form.register("email")}
                    className="form-field__input"
                  />
                  {step1Form.formState.errors.email && (
                    <p className="form-field__error">
                      {t(step1Form.formState.errors.email.message as string)}
                    </p>
                  )}
                </div>
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  disabled={!step1Form.formState.isValid || requestResetCodeMutation.isPending}
                  className="w-full"
                >
                  {t("resetPassword.sendCode", { ns: "auth" })}
                </Button>
              </form>
            )}

            {/* Step 2 */}
            {step === 2 && (
              <form onSubmit={step2Form.handleSubmit(onStep2)} noValidate>
                <p className="auth-card__subtitle">
                  {t("resetPassword.codeSent", {
                    ns: "auth",
                    email,
                  })}
                </p>
                <div className="form-field">
                  <label className="form-field__label">
                    {t("resetPassword.codeLabel", { ns: "auth" })}
                  </label>
                  <div className="inline-field">
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      {...codeReg}
                      onChange={(e) => {
                        e.target.value = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 6);
                        codeReg.onChange(e);
                      }}
                      className="form-field__input"
                    />
                    <span className="timer">{formatTimer(timerLeft)}</span>
                  </div>
                  {/* Inline error precedence: timer-expiry > schema/mismatch.
                      AC requires the standardized "expired" copy when the
                      5-min OTP window elapses, regardless of field state. */}
                  {isTimerExpired ? (
                    <p className="form-field__error" role="alert">
                      {t("validation:auth.codeExpired")}
                    </p>
                  ) : step2Form.formState.errors.code ? (
                    <p className="form-field__error">
                      {t(step2Form.formState.errors.code.message as string)}
                    </p>
                  ) : null}
                </div>
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  disabled={!step2Form.formState.isValid || isTimerExpired || verifyResetCodeMutation.isPending}
                  className="w-full"
                >
                  {t("resetPassword.verify", { ns: "auth" })}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  onClick={handleResend}
                  className="w-full mt-2 justify-center"
                >
                  {t("resetPassword.resend", { ns: "auth" })}
                </Button>
              </form>
            )}

            {/* Step 3 */}
            {step === 3 && (
              <form onSubmit={step3Form.handleSubmit(onStep3)} noValidate>
                <div className="form-field">
                  <label className="form-field__label">
                    {t("resetPassword.newPassword", { ns: "auth" })}
                  </label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    placeholder={t("resetPassword.newPasswordPlaceholder", {
                      ns: "auth",
                    })}
                    {...step3Form.register("password")}
                    className="form-field__input"
                  />
                  {step3Form.formState.errors.password && (
                    <p className="form-field__error">
                      {t(step3Form.formState.errors.password.message as string)}
                    </p>
                  )}
                </div>
                <div className="form-field">
                  <label className="form-field__label">
                    {t("resetPassword.confirmPassword", { ns: "auth" })}
                  </label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    {...step3Form.register("confirmPassword")}
                    className="form-field__input"
                  />
                  {step3Form.formState.errors.confirmPassword && (
                    <p className="form-field__error">
                      {t(
                        step3Form.formState.errors.confirmPassword
                          .message as string,
                      )}
                    </p>
                  )}
                </div>
                {step3Form.formState.errors.root && (
                  <p className="form-field__error" role="alert">
                    {t(step3Form.formState.errors.root.message as string)}
                  </p>
                )}
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  disabled={!step3Form.formState.isValid || confirmResetMutation.isPending}
                  className="w-full"
                >
                  {t("resetPassword.changePassword", { ns: "auth" })}
                </Button>
              </form>
            )}
          </>
        )}

        {/* Done */}
        {step === "done" && (
          <div className="empty-state">
            <h3 className="empty-state__title">
              {t("resetPassword.doneTitle", { ns: "auth" })}
            </h3>
            <p className="empty-state__desc">
              {t("resetPassword.doneMessage", { ns: "auth" })}
            </p>
            <div className="empty-state__action">
              <Button
                variant="primary"
                size="lg"
                onClick={() => navigate("/login", { replace: true })}
              >
                {t("resetPassword.backToLogin", { ns: "auth" })}
              </Button>
            </div>
          </div>
        )}

        {step !== "done" && (
          <div className="auth-card__links">
            <Link to="/login">
              {t("resetPassword.backLink", { ns: "auth" })}
            </Link>
          </div>
        )}
      </div>
    </main>
  );
};

export default ResetPasswordPage;
